import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { AlertCircle, ArrowLeft, Eye, EyeOff } from "lucide-react";
import type { ExtractedPlayer, ReviewRow, CommitPlayer } from "@/lib/roster-ingestion/types";

function toIntOrUndefined(s: string | null | undefined): number | undefined {
  if (!s) return undefined;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : undefined;
}

interface Props {
  players: ExtractedPlayer[];
  warnings: string[];
  eventId: string;
  teamId: string;
  onConfirm: (players: CommitPlayer[]) => void;
  onBack: () => void;
  onCancel: () => void;
  committing: boolean;
}

function buildReviewRows(players: ExtractedPlayer[]): ReviewRow[] {
  return players.map((p, i) => {
    const topCandidate = p.match_candidates[0];
    const hasAutoMatch =
      topCandidate && topCandidate.match_score >= 0.85 && p.match_candidates.length === 1;
    const hasAmbiguous =
      p.match_candidates.length > 0 && !hasAutoMatch;

    return {
      index: i,
      excluded: false,
      matchAction: hasAutoMatch ? "auto" : hasAmbiguous ? "manual" : "new",
      selectedCandidateId: hasAutoMatch ? topCandidate.player_id : null,
      first_name: p.first_name.value || "",
      last_name: p.last_name.value || "",
      jersey_number: p.jersey_number.value || "",
      grad_year: p.grad_year.value?.toString() || "",
      position: p.position.value || "",
      height_in: p.height_in.value?.toString() || "",
      dob: p.dob.value || "",
      email: p.email.value || "",
      parent_email: p.parent_email.value || "",
      home_state: p.home_state.value || "",
      candidates: p.match_candidates,
      confidences: {
        first_name: p.first_name.confidence,
        last_name: p.last_name.confidence,
        jersey_number: p.jersey_number.confidence,
        grad_year: p.grad_year.confidence,
        position: p.position.confidence,
        height_in: p.height_in.confidence,
        dob: p.dob.confidence,
        email: p.email.confidence,
        parent_email: p.parent_email.confidence,
        home_state: p.home_state.confidence,
      },
    };
  });
}

function CellInput({
  value,
  onChange,
  confidence,
  className = "",
  size = "default",
}: {
  value: string;
  onChange: (v: string) => void;
  confidence: number;
  className?: string;
  size?: "default" | "sm";
}) {
  const lowConf = confidence > 0 && confidence < 0.6;
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`${size === "sm" ? "h-8 text-xs px-1.5" : "h-8 text-sm px-2"} ${
        lowConf ? "italic border-dashed border-amber-300" : ""
      } ${className}`}
      title={lowConf ? `Low confidence: ${(confidence * 100).toFixed(0)}%` : undefined}
    />
  );
}

function getRowStatus(row: ReviewRow): { label: string; className: string } {
  if (row.excluded) return { label: "Excluded", className: "bg-gray-100 text-gray-600" };
  if (row.matchAction === "auto") return { label: "Auto-matched", className: "bg-green-100 text-green-800" };
  if (row.matchAction === "manual") return { label: "Ambiguous", className: "bg-amber-100 text-amber-800" };
  return { label: "New", className: "bg-blue-100 text-blue-800" };
}

export function RosterReviewStep({
  players,
  warnings,
  onConfirm,
  onBack,
  onCancel,
  committing,
}: Props) {
  const [rows, setRows] = useState<ReviewRow[]>(() => buildReviewRows(players));

  const updateRow = (index: number, patch: Partial<ReviewRow>) => {
    setRows((prev) =>
      prev.map((r) => (r.index === index ? { ...r, ...patch } : r))
    );
  };

  const counts = useMemo(() => {
    const active = rows.filter((r) => !r.excluded);
    return {
      total: rows.length,
      autoMatched: active.filter((r) => r.matchAction === "auto").length,
      ambiguous: active.filter((r) => r.matchAction === "manual").length,
      newPlayers: active.filter((r) => r.matchAction === "new").length,
      excluded: rows.filter((r) => r.excluded).length,
    };
  }, [rows]);

  const canConfirm = rows.some(
    (r) => !r.excluded && r.first_name.trim() && r.last_name.trim()
  );

  const handleConfirm = () => {
    const commitPlayers: CommitPlayer[] = rows
      .filter((r) => !r.excluded && r.first_name.trim() && r.last_name.trim())
      .map((r) => ({
        match_player_id:
          r.matchAction === "new" ? null : r.selectedCandidateId || null,
        first_name: r.first_name.trim(),
        last_name: r.last_name.trim(),
        date_of_birth: r.dob || undefined,
        grad_year: toIntOrUndefined(r.grad_year),
        primary_position: r.position || undefined,
        jersey_number: r.jersey_number || undefined,
        height_in: toIntOrUndefined(r.height_in),
        primary_email: r.email || undefined,
        parent_1_email: r.parent_email || undefined,
        home_state: r.home_state || undefined,
      }));

    onConfirm(commitPlayers);
  };

  return (
    <div className="space-y-4">
      {/* Summary banners */}
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">
          {counts.total} parsed
        </Badge>
        {counts.autoMatched > 0 && (
          <Badge className="bg-green-100 text-green-800 border-green-200">
            {counts.autoMatched} auto-matched
          </Badge>
        )}
        {counts.newPlayers > 0 && (
          <Badge className="bg-blue-100 text-blue-800 border-blue-200">
            {counts.newPlayers} new
          </Badge>
        )}
        {counts.ambiguous > 0 && (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
            {counts.ambiguous} ambiguous
          </Badge>
        )}
        {counts.excluded > 0 && (
          <Badge className="bg-gray-100 text-gray-600 border-gray-200">
            {counts.excluded} excluded
          </Badge>
        )}
      </div>

      {warnings.length > 0 && (
        <div className="flex gap-2 rounded-md bg-amber-50 p-3 text-sm text-amber-700">
          <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
          <div>
            {warnings.map((w, i) => (
              <p key={i}>{w}</p>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto border rounded-md">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="px-2 py-2 text-left text-xs font-medium w-[100px]">Status</th>
              <th className="px-2 py-2 text-left text-xs font-medium">First</th>
              <th className="px-2 py-2 text-left text-xs font-medium">Last</th>
              <th className="px-2 py-2 text-left text-xs font-medium w-[90px]">DOB</th>
              <th className="px-2 py-2 text-left text-xs font-medium w-[60px]">Grad</th>
              <th className="px-2 py-2 text-left text-xs font-medium w-[60px]">Pos</th>
              <th className="px-2 py-2 text-left text-xs font-medium w-[50px]">#</th>
              <th className="px-2 py-2 text-left text-xs font-medium w-[60px]">Ht</th>
              <th className="px-2 py-2 text-left text-xs font-medium">Email</th>
              <th className="px-2 py-2 text-left text-xs font-medium">Parent email</th>
              <th className="px-2 py-2 text-left text-xs font-medium w-[50px]">ST</th>
              <th className="px-2 py-2 text-left text-xs font-medium w-[60px]"></th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {rows.map((row) => {
              const status = getRowStatus(row);
              return (
                <tr
                  key={row.index}
                  className={row.excluded ? "opacity-40" : undefined}
                >
                  <td className="px-2 py-1.5">
                    <div className="space-y-1">
                      <Badge
                        variant="outline"
                        className={`text-[10px] ${status.className}`}
                      >
                        {status.label}
                      </Badge>
                      {row.candidates.length > 0 && !row.excluded && (
                        <Select
                          value={row.matchAction === "new" ? "__new__" : (row.selectedCandidateId || "__new__")}
                          onValueChange={(v) => {
                            if (v === "__new__") {
                              updateRow(row.index, {
                                matchAction: "new",
                                selectedCandidateId: null,
                              });
                            } else {
                              updateRow(row.index, {
                                matchAction: "manual",
                                selectedCandidateId: v,
                              });
                            }
                          }}
                        >
                          <SelectTrigger className="h-6 text-[10px] w-[90px]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__new__">Create new</SelectItem>
                            {row.candidates.map((c) => (
                              <SelectItem key={c.player_id} value={c.player_id}>
                                {c.display_name} ({(c.match_score * 100).toFixed(0)}%)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </td>
                  <td className="px-1 py-1">
                    <CellInput
                      value={row.first_name}
                      onChange={(v) => updateRow(row.index, { first_name: v })}
                      confidence={row.confidences.first_name}
                      size="sm"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <CellInput
                      value={row.last_name}
                      onChange={(v) => updateRow(row.index, { last_name: v })}
                      confidence={row.confidences.last_name}
                      size="sm"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <CellInput
                      value={row.dob}
                      onChange={(v) => updateRow(row.index, { dob: v })}
                      confidence={row.confidences.dob}
                      size="sm"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <CellInput
                      value={row.grad_year}
                      onChange={(v) => updateRow(row.index, { grad_year: v })}
                      confidence={row.confidences.grad_year}
                      size="sm"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <CellInput
                      value={row.position}
                      onChange={(v) => updateRow(row.index, { position: v })}
                      confidence={row.confidences.position}
                      size="sm"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <CellInput
                      value={row.jersey_number}
                      onChange={(v) => updateRow(row.index, { jersey_number: v })}
                      confidence={row.confidences.jersey_number}
                      size="sm"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <CellInput
                      value={row.height_in}
                      onChange={(v) => updateRow(row.index, { height_in: v })}
                      confidence={row.confidences.height_in}
                      size="sm"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <CellInput
                      value={row.email}
                      onChange={(v) => updateRow(row.index, { email: v })}
                      confidence={row.confidences.email}
                      size="sm"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <CellInput
                      value={row.parent_email}
                      onChange={(v) => updateRow(row.index, { parent_email: v })}
                      confidence={row.confidences.parent_email}
                      size="sm"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <CellInput
                      value={row.home_state}
                      onChange={(v) => updateRow(row.index, { home_state: v })}
                      confidence={row.confidences.home_state}
                      size="sm"
                    />
                  </td>
                  <td className="px-1 py-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      onClick={() =>
                        updateRow(row.index, { excluded: !row.excluded })
                      }
                      title={row.excluded ? "Include" : "Exclude"}
                    >
                      {row.excluded ? (
                        <EyeOff className="h-3.5 w-3.5" />
                      ) : (
                        <Eye className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Actions */}
      <div className="flex justify-between pt-2">
        <Button variant="ghost" onClick={onBack} disabled={committing}>
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onCancel} disabled={committing}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm || committing}>
            Confirm and add to roster
          </Button>
        </div>
      </div>
    </div>
  );
}
